import React, { useState, useEffect } from 'react';

/**
 * Saison-/Event-Countdown-Banner mit Auto-Zyklus.
 *
 * Verhalten:
 *  - SAISONEN (spring/summer/autumn/winter, 'standard', 'auto', null):
 *    Der Banner zyklt automatisch durch das Jahr:
 *      • Vor Saison-Start  → Countdown zur nächsten Saison
 *      • Während Saison    → "[Saison]-zeit ist da · Lass dich inspirieren!"
 *      • Saisonende        → Countdown zur darauffolgenden Saison startet automatisch
 *    Saison-Übergänge sind hartcodiert nach astronomischem Kalender:
 *      Frühling 20.3.  ·  Sommer 21.6.  ·  Herbst 23.9.  ·  Winter 21.12.
 *
 *  - EINTAGS-EVENTS (christmas/easter/mothersday/valentines):
 *    explizit gesetzt → zeigt "Heute ist …!" bzw. Countdown zu THIS YEAR
 *    Wenn das Event vorbei ist (+ Karenzzeit) wechselt der Banner zurück
 *    auf den Saison-Zyklus.
 *
 *  - FIXED-YEAR-EVENTS (newyear 2026): einmaliges Event ohne Roll-over.
 */

const SEASONS_CYCLE = [
  { key: 'spring', name: 'Frühlingsanfang', liveName: 'Frühlingszeit', icon: '🌷', month: 2,  day: 20 },
  { key: 'summer', name: 'Sommeranfang',    liveName: 'Sommerzeit',    icon: '☀️', month: 5,  day: 21 },
  { key: 'autumn', name: 'Herbstanfang',    liveName: 'Herbstzeit',    icon: '🍂', month: 8,  day: 23 },
  { key: 'winter', name: 'Winteranfang',    liveName: 'Winterzeit',    icon: '❄️', month: 11, day: 21 },
];

const SEASON_PALETTE = {
  spring: 'bg-gradient-to-r from-pink-300 to-yellow-300 text-white',
  summer: 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white',
  autumn: 'bg-gradient-to-r from-[#EBCBB2] to-[#E2B898] text-[#5D4E3C]',
  winter: 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white',
};

const DAY_EVENTS = {
  christmas:  { name: 'Weihnachten',  icon: '🎄', month: 11, day: 25, keepAfterDays: 2, palette: 'bg-gradient-to-r from-red-600 to-green-600 text-white' },
  easter:     { name: 'Ostern',       icon: '🐰', month: 3,  day: 20, keepAfterDays: 1, palette: 'bg-gradient-to-r from-pink-400 to-purple-400 text-white' },
  mothersday: { name: 'Muttertag',    icon: '💐', month: 4,  day: 11, keepAfterDays: 0, palette: 'bg-gradient-to-r from-pink-500 to-rose-500 text-white' },
  valentines: { name: 'Valentinstag', icon: '💕', month: 1,  day: 14, keepAfterDays: 0, palette: 'bg-gradient-to-r from-red-500 to-pink-500 text-white' },
};

const FIXED_EVENTS = {
  newyear: { name: 'Neujahr 2026', icon: '🎆', year: 2026, month: 0, day: 1, keepAfterDays: 1, palette: 'bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white' },
};

// Liefert {currentSeason, nextSeason, nextStart} basierend auf jetzt
const resolveSeasonState = (now) => {
  const y = now.getFullYear();
  // Erzeuge die nächsten 5 Saison-Starts (über zwei Jahre verteilt) und sortiere
  const upcoming = [];
  for (let i = -1; i <= 1; i++) {
    SEASONS_CYCLE.forEach((s) => {
      upcoming.push({ ...s, date: new Date(y + i, s.month, s.day) });
    });
  }
  upcoming.sort((a, b) => a.date - b.date);

  // Finde die letzte Saison, die bereits begonnen hat → das ist die aktuelle Saison
  let current = null;
  let next = null;
  for (let i = 0; i < upcoming.length; i++) {
    if (upcoming[i].date <= now) current = upcoming[i];
    if (upcoming[i].date > now) { next = upcoming[i]; break; }
  }
  return { current, next };
};

const formatDiff = (target, now) => {
  const diff = target - now;
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const SeasonalCountdown = ({ theme }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const wrapper = (palette, content) => (
    <div className={`${palette} py-1 px-3 text-center text-[11px] sm:text-xs`}>
      <div className="max-w-4xl mx-auto flex items-center justify-center gap-1.5 flex-wrap">
        {content}
      </div>
    </div>
  );

  const renderCountdown = (palette, prefix, target, suffix) => {
    const t = formatDiff(target, now);
    if (!t) return null;
    return wrapper(palette, <>
      {prefix && <span className="opacity-90">{prefix}</span>}
      {t.days > 0 && <span className="bg-white/20 px-1.5 py-[1px]"><strong>{t.days}</strong>&nbsp;Tag{t.days !== 1 ? 'e' : ''}</span>}
      {(t.days > 0 || t.hours > 0) && <span className="bg-white/20 px-1.5 py-[1px]"><strong>{t.hours}</strong>&nbsp;Std</span>}
      <span className="bg-white/20 px-1.5 py-[1px]"><strong>{t.minutes}</strong>&nbsp;Min</span>
      <span className="bg-white/20 px-1.5 py-[1px] hidden sm:inline"><strong>{t.seconds}</strong>&nbsp;Sek</span>
      <span className="opacity-90">{suffix}</span>
    </>);
  };

  // -------- Eintags-Event explizit gesetzt? --------
  if (theme && DAY_EVENTS[theme]) {
    const e = DAY_EVENTS[theme];
    const y = now.getFullYear();
    const eventDate = new Date(y, e.month, e.day);
    const todayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const isToday = todayKey(now) === todayKey(eventDate);
    const daysSince = Math.floor((now - eventDate) / 86400000);
    if (isToday) {
      return wrapper(e.palette, <span className="font-semibold tracking-wide">{e.icon} Heute ist {e.name}!</span>);
    }
    if (daysSince > 0 && daysSince <= e.keepAfterDays) return null; // dieses Jahr aus
    if (now < eventDate) {
      return renderCountdown(e.palette, 'Noch', eventDate, `bis ${e.name}!`);
    }
    // Vorbei → falle in den Saison-Zyklus zurück
  }

  // -------- Fixed-Year-Event explizit? --------
  if (theme && FIXED_EVENTS[theme]) {
    const e = FIXED_EVENTS[theme];
    const target = new Date(e.year, e.month, e.day);
    if (now >= target) {
      const daysSince = Math.floor((now - target) / 86400000);
      if (daysSince <= e.keepAfterDays) {
        return wrapper(e.palette, <span className="font-semibold tracking-wide">{e.icon} {e.name} ist da!</span>);
      }
      return null;
    }
    return renderCountdown(e.palette, 'Noch', target, `bis ${e.name}!`);
  }

  // -------- SAISON-ZYKLUS (immer aktiv) --------
  const { current, next } = resolveSeasonState(now);
  if (!current || !next) return null;

  // Während der Saison wird durchgehend "[Saison] ist da" angezeigt.
  // Erst 21 Tage (3 Wochen) vor Beginn der nächsten Saison startet der Countdown.
  const daysUntilNext = Math.floor((next.date - now) / 86400000);
  const palette = SEASON_PALETTE[current.key] || SEASON_PALETTE.summer;
  const COUNTDOWN_LEAD_DAYS = 21;

  if (daysUntilNext > COUNTDOWN_LEAD_DAYS) {
    return wrapper(palette, (
      <span className="font-semibold tracking-wide">
        {current.icon} {current.liveName} ist da · Lass dich inspirieren!
      </span>
    ));
  }

  // Letzte 3 Wochen der Saison: Countdown zur NÄCHSTEN Saison (mit Palette der nächsten Saison für sanften Übergang)
  const palettesNext = SEASON_PALETTE[next.key] || palette;
  return renderCountdown(palettesNext, `${current.icon} Noch`, next.date, `bis ${next.name}!`);
};

export default SeasonalCountdown;
